import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorRendererOptions,
	CreatorState,
	CreatorStep,
	CreatorScreenshotCamera,
} from '@/types/creator';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { UUID } from '@/types/types';
import { Project, ProjectContact } from '@/api/ProjectApi';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { omit } from 'lodash';
import UmamiApi, { UmamiEvent } from '@/api/UmamiApi';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import APP_CONFIG from '@/configs/appConfig';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);

const isAdmin = searchParams.get(APP_CONFIG.IS_ADMIN_PARAM);

const renderer = ProjectRenderer.getInstance();

const initialState: CreatorState = {
	creatorProductModules: [],
	creatorLandscapeModules: [],
	name: '',
	contact: {
		fullName: '',
		email: '',
		phonePrefix: '',
		phoneNumber: '',
		companyName: '',
		vatNumber: '',
		address: '',
		comment: '',
	},
	renderer: {
		zoomLevel: RENDERER_CONFIG.INITIAL_ZOOM,
		loading: false,
		distance: RENDERER_CONFIG.INITIAL_DISTANCE,
	},
	options: {
		isPreviewMode: false,
		selectedShape: null,
		selectedPlaygroundFilling: null,
		customBackground: {
			attachment: null,
			dimensions: [0, 0],
			rotation: 0,
		},
		isMobileAsideOpened: true,
		currentStep: CreatorStep.SHAPES,
		isLoading: false,
		isDrawing: false,
		isMeasuring: false,
		screenshotPopoverOpened: false,
		creatorMeasurements: [],
		selectedCreatorModule: null,
		productModulesDrawerOpened: false,
		infoProductModule: null,
		landscapeModulesDrawerOpened: false,
		successDrawerOpened: false,
		instructionModalOpened: false,
		activeCamera: APP_CONFIG.DEFAULT_CAMERA,
		areCodesVisible: false,
		playgroundSurfaceArea: 0,
		transformControlsMode: APP_CONFIG.DEFAULT_TRANSFORM_MODE,
		screenshotCameras: [],
		moduleContextMenuData: {
			moduleRefId: null,
			position: [0, 0],
			type: 'product',
		},
	},
	project: null,
	isAdmin: !!isAdmin,
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		loadProject: (state, { payload }: PayloadAction<Project>) => {
			const { configuration, contact } = payload;

			state.name = payload.name;
			state.project = omit(payload, 'configuration');

			state.contact = contact;
			state.creatorProductModules = configuration.creatorProductModules;
			state.creatorLandscapeModules = configuration.creatorLandscapeModules;

			state.options = configuration.options.state.options;
			state.options.currentStep = CreatorStep.SHAPES;
			state.options.isPreviewMode = !state.isAdmin;
			state.options.areCodesVisible = false;
			state.options.activeCamera = APP_CONFIG.DEFAULT_CAMERA;

			// LEGACY
			if (!state.options.moduleContextMenuData)
				state.options.moduleContextMenuData = {
					moduleRefId: null,
					position: [0, 0],
					type: 'product',
				};

			state.renderer = configuration.options.state.renderer;
		},
		copyProject: (state) => {
			const url = new URL(window.location.href);
			const searchParams = new URLSearchParams(url.search);

			searchParams.delete(APP_CONFIG.PREVIEW_PARAM);

			url.search = searchParams.toString();
			window.history.replaceState({}, '', url.toString());

			state.options.isPreviewMode = false;
		},
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateContact: (
			state,
			{ payload }: PayloadAction<Partial<ProjectContact>>
		) => {
			Object.assign(state.contact, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.SELECT_MODULE:
					state.options.selectedCreatorModule =
						payload.data as ThreeEvents[ThreeEvent.SELECT_MODULE];
					break;
				case ThreeEvent.CALCULATE_SURFACE_AREA:
					state.options.playgroundSurfaceArea =
						payload.data as ThreeEvents[ThreeEvent.CALCULATE_SURFACE_AREA];
					break;
				case ThreeEvent.ZOOM_LEVEL:
					state.renderer.zoomLevel =
						payload.data as ThreeEvents[ThreeEvent.ZOOM_LEVEL];
					break;
				case ThreeEvent.DISTANCE:
					state.renderer.distance =
						payload.data as ThreeEvents[ThreeEvent.DISTANCE];
					break;
				case ThreeEvent.IS_DRAWING:
					state.options.isDrawing =
						payload.data as ThreeEvents[ThreeEvent.IS_DRAWING];
					break;
				case ThreeEvent.SET_MEASUREMENTS:
					state.options.creatorMeasurements =
						payload.data as ThreeEvents[ThreeEvent.SET_MEASUREMENTS];
					break;
				case ThreeEvent.CONTEXT_MENU: {
					const data = payload.data as ThreeEvents[ThreeEvent.CONTEXT_MENU];

					Object.assign(state.options.moduleContextMenuData, data);
					break;
				}
				default:
					throw new Error('UNKNOW_EVENT');
			}
		},
		setCreatorStep: (state, { payload }: PayloadAction<'prev' | 'next'>) => {
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;

			state.options.isMeasuring = false;
		},
		selectShape: (
			state,
			{ payload }: PayloadAction<CreatorOptions['selectedShape']>
		) => {
			if (!state.options.selectedShape)
				UmamiApi.emit(UmamiEvent.CONFIGURATION_STARTED);

			state.options.selectedShape = payload;
		},
		selectPlaygroundFilling: (
			state,
			{ payload }: PayloadAction<CreatorOptions['selectedPlaygroundFilling']>
		) => {
			state.options.selectedPlaygroundFilling = payload;
		},
		updateCustomBackground: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions['customBackground']>>
		) => {
			Object.assign(state.options.customBackground, payload);

			if (!payload.attachment) renderer.projectPlane.reset();
		},
		applyCustomBackground: ({ options }) => {
			if (!options.customBackground) return;

			renderer.projectPlane.handleCustomPlane(
				current(options.customBackground)
			);
		},
		setActiveCamera: (
			state,
			{ payload }: PayloadAction<CreatorOptions['activeCamera']>
		) => {
			state.options.isMeasuring = false;
			state.options.activeCamera = payload;
		},
		addCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			state.creatorProductModules.push(payload);
			state.options.productModulesDrawerOpened = false;
		},
		removeCreatorProductModule: (state, { payload }: PayloadAction<UUID>) => {
			state.creatorProductModules = state.creatorProductModules.filter(
				(item) => item.refId !== payload
			);
		},
		addCreatorLandscapeModule: (
			state,
			{ payload }: PayloadAction<CreatorLandscapeModule>
		) => {
			state.creatorLandscapeModules.push(payload);
			state.options.landscapeModulesDrawerOpened = false;
		},
		removeCreatorLandscapeModule: (state, { payload }: PayloadAction<UUID>) => {
			state.creatorLandscapeModules = state.creatorLandscapeModules.filter(
				(item) => item.refId !== payload
			);
		},
		setTransformMode: (
			state,
			{ payload }: PayloadAction<CreatorOptions['transformControlsMode']>
		) => {
			state.options.transformControlsMode = payload;
			renderer.transformController.setMode(payload);
		},
		setCodesVisibility: (state, { payload }: PayloadAction<boolean>) => {
			state.options.areCodesVisible = payload;
			renderer.modulesManager.areCodesVisible = payload;
		},
		setMeasuring: (state, { payload }: PayloadAction<boolean>) => {
			if (state.options.isMeasuring === payload) return;
			state.options.isMeasuring = payload;
		},
		removeMeasurement: (state, { payload }: PayloadAction<UUID>) => {
			state.options.creatorMeasurements =
				state.options.creatorMeasurements.filter((item) => item.id !== payload);
		},
		addScreenshotCamera: (
			state,
			{ payload }: PayloadAction<CreatorScreenshotCamera>
		) => {
			state.options.screenshotCameras.push(payload);
		},
		removeScreenshotCamera: (state, { payload }: PayloadAction<UUID>) => {
			state.options.screenshotCameras = state.options.screenshotCameras.filter(
				(camera) => camera.id !== payload
			);
		},
		reorderScreenshotCameras: (
			state,
			{ payload }: PayloadAction<{ idx: number; direction: 'up' | 'down' }>
		) => {
			const { idx, direction } = payload;
			const cameras = state.options.screenshotCameras;

			if (direction === 'up' && idx > 0)
				[cameras[idx - 1], cameras[idx]] = [cameras[idx], cameras[idx - 1]];
			else if (direction === 'down' && idx < cameras.length - 1)
				[cameras[idx + 1], cameras[idx]] = [cameras[idx], cameras[idx + 1]];

			state.options.screenshotCameras = [...cameras];
		},
		handleCameraUptate: (
			state,
			{ payload }: PayloadAction<Partial<CreatorScreenshotCamera>>
		) => {
			for (const camera of state.options.screenshotCameras) {
				if (camera.id !== payload.id) continue;

				Object.assign(camera, payload);
				break;
			}
		},
		resetCreator: (state) => {},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
